import React, { useState, useEffect } from "react";
import { MapPin, Mail, Building } from "lucide-react";
import { supabase } from "../utils/supabaseClient";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/About.css";

const About = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    address: "",
    email: "",
    mapsLink: "",
  });

  useEffect(() => {
    fetchHeadOfficeDetails();
  }, []);

  const fetchHeadOfficeDetails = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("head_office")
        .select("*")
        .single();

      if (error) throw error;

      if (data) {
        setFormData({
          address: data.address || "",
          email: data.email || "",
          mapsLink: data.maps_link || "",
        });
      }
    } catch (error) {
      toast.error("Error fetching head office details");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase.from("head_office").upsert({
        id: 1,
        address: formData.address,
        email: formData.email,
        maps_link: formData.mapsLink,
        updated_at: new Date(),
      });

      if (error) throw error;

      toast.success("Head office details updated successfully!");
      setIsEditing(false);
    } catch (error) {
      toast.error("Error updating head office details");
      console.error(error);
    }
  };

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="loading-state">Loading head office details...</div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="about-header">
        <h2>Head Office Details</h2>
        {!isEditing && (
          <button className="edit-button" onClick={() => setIsEditing(true)}>
            Edit Details
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="about-container">
          <form onSubmit={handleSubmit} className="head-office-form">
            <div className="form-group">
              <label htmlFor="address">
                <Building size={20} />
                Address
              </label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">
                <Mail size={20} />
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="mapsLink">
                <MapPin size={20} />
                Google Maps Embed Link
              </label>
              <input
                type="url"
                id="mapsLink"
                name="mapsLink"
                value={formData.mapsLink}
                onChange={handleInputChange}
                placeholder="https://www.google.com/maps/embed?..."
                required
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-button">
                Save Changes
              </button>
              <button
                type="button"
                className="cancel-button"
                onClick={() => {
                  setIsEditing(false);
                  fetchHeadOfficeDetails();
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="about-container">
          <div className="details-card">
            <div className="detail-item">
              <Building size={24} />
              <div>
                <h4>Address</h4>
                <p>{formData.address}</p>
              </div>
            </div>

            <div className="detail-item">
              <Mail size={24} />
              <div>
                <h4>Email</h4>
                <p>{formData.email}</p>
              </div>
            </div>

            {formData.mapsLink && (
              <div className="maps-container">
                <iframe
                  src={formData.mapsLink}
                  width="100%"
                  height="450"
                  style={{ border: 0, borderRadius: "10px" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Head Office Location"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default About;
